import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useContractorProfile } from './hooks/useContractorProfile';
import DocumentViewer from '../../components/viewers/DocumentViewer';
import MarkdownEditor from '../../components/editor/MarkdownEditor';
import { ProfilePictureManager } from '../enhancer/components/ProfilePictureManager';
import SharedSidebar from '../../components/layout/SharedSidebar';
import { generateProfileFilename, extractNameFromMarkdown, getFallbackFilename } from '../../utils/fileNaming';
import { useAuth } from '../../contexts/AuthContext';
import { EntraService } from '../enhancer/services/entraService';
import { useMsal } from '@azure/msal-react';
import ConsentModal from './components/ConsentModal';
import { Alert, Snackbar } from '@mui/material';
import './ContractorProfile.css';

const ContractorProfile: React.FC = () => {
  const processing = useContractorProfile();
  const { user } = useAuth();
  const { instance } = useMsal();
  const entraServiceRef = useRef<EntraService | null>(null);

  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [hasUploadedFile, setHasUploadedFile] = useState(false);
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [generatedPdfFile, setGeneratedPdfFile] = useState<File | null>(null);
  const [generatedPdfUrl, setGeneratedPdfUrl] = useState<string | null>(null);
  const [hasExportedPDF, setHasExportedPDF] = useState(false);
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  const [consentModalOpen, setConsentModalOpen] = useState(false);
  const [isSubmittingConsent, setIsSubmittingConsent] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const sharePointFilename = useMemo(() => {
    const { firstName, lastName } = extractNameFromMarkdown(markdownContent);
    if (firstName || lastName) {
      return generateProfileFilename({ firstName, lastName, format: 'pdf', variant: 'ic' });
    }
    return getFallbackFilename('profile', 'pdf', undefined, 'ic');
  }, [markdownContent]);

  useEffect(() => {
    return () => {
      if (generatedPdfUrl) {
        URL.revokeObjectURL(generatedPdfUrl);
      }
    };
  }, [generatedPdfUrl]);

  useEffect(() => {
    if (instance) {
      entraServiceRef.current = new EntraService(instance);
    }
  }, [instance]);

  useEffect(() => {
    const initAndLoad = async () => {
      if (!user?.localAccountId || !entraServiceRef.current) return;

      try {
        setIsLoadingProfile(true);
        await loadProfileFromAzure();
      } catch (error) {
        console.error('Failed to load profile:', error);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    initAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadProfileFromAzure = async () => {
    if (!entraServiceRef.current || !user?.localAccountId) return;

    try {
      const profileData = await entraServiceRef.current.getProfile();

      if (profileData && profileData.profile_markdown) {
        setMarkdownContent(profileData.profile_markdown);
      }
    } catch (error) {
      console.error('Failed to load profile from Azure:', error);
    }
  };

  const saveProfileToAzure = async (content: string) => {
    if (!entraServiceRef.current) return;

    setIsSavingProfile(true);
    try {
      await entraServiceRef.current.saveProfile(content);
    } catch (error) {
      console.error('Failed to save profile to Azure:', error);
      setErrorMessage('Failed to save profile. Please try again.');
    } finally {
      setIsSavingProfile(false);
    }
  };

  useEffect(() => {
    if (processing.uploadedDocument && !hasUploadedFile) {
      setHasUploadedFile(true);
      setSidebarOpen(false);
      setMarkdownContent('');
    }
  }, [processing.uploadedDocument, hasUploadedFile]);

  useEffect(() => {
    if (!processing.uploadedDocument && hasUploadedFile) {
      setHasUploadedFile(false);
      setSidebarOpen(true);
    }
  }, [processing.uploadedDocument, hasUploadedFile]);

  useEffect(() => {
    if (processing.processingResult?.redacted_content) {
      const content = processing.processingResult.redacted_content;
      setMarkdownContent(content);

      if (entraServiceRef.current) {
        saveProfileToAzure(content);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processing.processingResult]);

  const handleFileUpload = async (files: FileList) => {
    if (files.length > 0) {
      const file = files[0];
      processing.resetState();

      if (generatedPdfUrl) {
        URL.revokeObjectURL(generatedPdfUrl);
      }

      setMarkdownContent('');
      setGeneratedPdfFile(null);
      setGeneratedPdfUrl(null);
      setHasExportedPDF(false);

      await processing.uploadFile(file);
    }
  };

  const handleGeneratePDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const content = markdownContent || processing.processingResult?.redacted_content || '';

      if (content && entraServiceRef.current) {
        try {
          await saveProfileToAzure(content);
        } catch (saveError) {
          console.error('Failed to auto-save before PDF export:', saveError);
        }
      }

      const profilePhoto = profilePicture;
      const userName = user?.name || null;

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:9110'}/api/pdf/markdown-to-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          markdown_content: content,
          style: 'profiler',
          theme: 'athlete',
          filename: 'profile.pdf',
          profile_photo: profilePhoto,
          user_name: userName
        }),
      });

      if (!response.ok) {
        throw new Error('PDF generation failed');
      }

      const blob = await response.blob();
      const { firstName, lastName } = extractNameFromMarkdown(markdownContent);
      const filename = firstName || lastName
        ? generateProfileFilename({ firstName, lastName, format: 'pdf', variant: 'ic' })
        : getFallbackFilename('profile', 'pdf', undefined, 'ic');
      const pdfFile = new File([blob], filename, {
        type: 'application/pdf',
        lastModified: Date.now()
      });

      if (generatedPdfUrl) {
        URL.revokeObjectURL(generatedPdfUrl);
      }

      setGeneratedPdfFile(pdfFile);
      setGeneratedPdfUrl(URL.createObjectURL(pdfFile));
      setHasExportedPDF(true);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setErrorMessage('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleConsent = async () => {
    if (!generatedPdfFile) return;

    setIsSubmittingConsent(true);
    try {
      await processing.giveConsent(generatedPdfFile, sharePointFilename, profilePicture);
      setConsentModalOpen(false);
      setSuccessMessage('Profile submitted successfully. Your profile has been saved to SharePoint.');
    } catch (error) {
      setErrorMessage('Failed to submit profile. Please try again.');
    } finally {
      setIsSubmittingConsent(false);
    }
  };

  // Withdrawal is an email opt-out request to Project Support, not an in-app
  // API delete: open the user's mail client with a pre-filled draft. Project
  // Support processes the request. (Restores the pre-#17 behaviour.)
  const handleWithdraw = () => {
    const userName = user?.name || '';
    const subject = encodeURIComponent('Withdraw consent for profile use');
    const body = encodeURIComponent(
      'Dear Project Support,\n\n' +
      'I wish to withdraw my consent for SSI to use my professional profile in client interactions.\n\n' +
      `Name: ${userName}\n\nRegards,\n${userName}`
    );
    window.location.href = `mailto:ProjectSupport@ssistrategy.com?subject=${subject}&body=${body}`;
  };

  const handleDownload = () => {
    if (!generatedPdfFile) return;

    const url = URL.createObjectURL(generatedPdfFile);
    const link = document.createElement('a');
    link.href = url;
    link.download = sharePointFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const isConsented = processing.consent?.consented === true;

  return (
    <div className="contractor-profile-container">
      <SharedSidebar
        isOpen={isSidebarOpen}
        onToggle={() => setSidebarOpen(!isSidebarOpen)}
        title="My Profile"
        subtitle="Upload your CV to create your professional profile"
      >
        <div className="cv-manager">
          {/* Profile Picture */}
          <div className="sidebar-section">
            <ProfilePictureManager
              onPictureUpdate={(pictureUrl) => setProfilePicture(pictureUrl)}
            />
          </div>

          {/* Upload */}
          <div className="sidebar-section">
            <div className="section-title">
              <span className="material-icons">cloud_upload</span>
              Upload Your CV
            </div>
            <div
              className="upload-area"
              onDrop={(e) => {
                e.preventDefault();
                handleFileUpload(e.dataTransfer.files);
              }}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => document.getElementById('contractor-file-input')?.click()}
            >
              <div className="upload-icon">
                <span className="material-icons">upload_file</span>
              </div>
              <div className="upload-text">Drop your CV (PDF or DOCX) here</div>
              <button className="upload-button">Choose File</button>
            </div>
            <input
              id="contractor-file-input"
              type="file"
              accept=".pdf,.docx"
              style={{ display: 'none' }}
              onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
            />
          </div>

          {/* Consent Status */}
          <div className="sidebar-section">
            <div className="section-title">
              <span className="material-icons">verified_user</span>
              Profile Status
            </div>
            <div className={`consent-status ${isConsented ? 'active' : processing.consent?.withdrawn_at ? 'withdrawn' : 'pending'}`}>
              {processing.isLoadingConsent ? (
                <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px' }}>Loading...</p>
              ) : isConsented ? (
                <>
                  <span className="material-icons" style={{ fontSize: '32px', color: '#16a34a', display: 'block', marginBottom: '8px' }}>check_circle</span>
                  <p style={{ color: '#16a34a', fontSize: '14px', fontWeight: 500 }}>Profile Submitted</p>
                  {processing.consent?.consented_at && (
                    <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', marginTop: '4px' }}>
                      Since {new Date(processing.consent.consented_at).toLocaleDateString()}
                    </p>
                  )}
                </>
              ) : processing.consent?.withdrawn_at ? (
                <>
                  <span className="material-icons" style={{ fontSize: '32px', color: '#ef4444', display: 'block', marginBottom: '8px' }}>remove_circle</span>
                  <p style={{ color: '#ef4444', fontSize: '14px', fontWeight: 500 }}>Consent Withdrawn</p>
                  <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', marginTop: '4px' }}>
                    You can re-submit at any time
                  </p>
                </>
              ) : (
                <>
                  <span className="material-icons" style={{ fontSize: '32px', color: 'rgba(255, 255, 255, 0.3)', display: 'block', marginBottom: '8px' }}>pending</span>
                  <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px' }}>Not yet submitted</p>
                  <p style={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '12px', marginTop: '4px' }}>
                    Upload your CV and generate a PDF to submit
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </SharedSidebar>

      <div className="workspace">
        {(processing.uploadedDocument || hasUploadedFile || markdownContent) ? (
          <div className="panels-container two-panel-layout">
            {/* Markdown Editor Panel */}
            <div className="panel editor-panel">
              <div className="panel-header">
                <div className="panel-title">
                  <span>Profile Editor</span>
                </div>
                <div className="panel-controls">
                  <button
                    className="control-btn"
                    title="Save"
                    onClick={() => markdownContent && saveProfileToAzure(markdownContent)}
                    disabled={isSavingProfile || !markdownContent}
                  >
                    <span className="material-icons">{isSavingProfile ? 'hourglass_empty' : 'save'}</span>
                  </button>
                  <button
                    className="control-btn"
                    title="Reset to original"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to reset to the original content?')) {
                        setMarkdownContent(processing.processingResult?.redacted_content || '');
                      }
                    }}
                  >
                    <span className="material-icons">refresh</span>
                  </button>
                  <button
                    className="export-btn"
                    onClick={handleGeneratePDF}
                    disabled={isGeneratingPDF || (!markdownContent && !processing.processingResult)}
                  >
                    <span className="material-icons">picture_as_pdf</span>
                    {isGeneratingPDF ? 'Generating...' : 'Export PDF'}
                  </button>
                </div>
              </div>
              <div className="panel-content" style={{ position: 'relative' }}>
                {isGeneratingPDF && (
                  <div style={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)', textAlign: 'center', zIndex: 10,
                    background: 'rgba(255, 255, 255, 0.95)', padding: '20px',
                    borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <div className="spinner" style={{ marginBottom: '16px', margin: '0 auto' }}></div>
                    <p style={{ color: 'var(--stone-grey)' }}>Generating PDF...</p>
                  </div>
                )}
                {processing.isUploading && (
                  <div style={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)', textAlign: 'center', zIndex: 10,
                    background: 'rgba(255, 255, 255, 0.95)', padding: '20px',
                    borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <div className="spinner" style={{ marginBottom: '16px' }}></div>
                    <p style={{ color: 'var(--stone-grey)' }}>Uploading document...</p>
                  </div>
                )}
                {processing.isProcessing && !processing.processingResult && (
                  <div style={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)', textAlign: 'center', zIndex: 10,
                    background: 'rgba(255, 255, 255, 0.95)', padding: '20px',
                    borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <div className="spinner" style={{ marginBottom: '16px' }}></div>
                    <p style={{ color: 'var(--stone-grey)' }}>Formatting your profile...</p>
                    <small style={{ color: 'var(--stone-grey)', fontSize: '12px' }}>Aligning to SSI professional profile template</small>
                  </div>
                )}
                <MarkdownEditor
                  value={markdownContent || processing.processingResult?.redacted_content || '# Your Profile\n\nUpload your CV to generate your professional profile.'}
                  onChange={setMarkdownContent}
                  height={500}
                  placeholder="Your professional profile will appear here..."
                  readOnly={(!processing.processingResult && !markdownContent) || isLoadingProfile}
                />
              </div>
            </div>

            {/* Generated PDF Panel */}
            <div className={`panel ${!hasExportedPDF ? 'hide' : ''}`}>
              <div className="panel-header">
                <div className="panel-title">
                  <span>Profile Preview</span>
                </div>
                <div className="panel-controls">
                  <button className="download-btn" onClick={handleDownload}>
                    <span className="material-icons">download</span>
                    Download
                  </button>
                  {isConsented ? (
                    <button
                      className="withdraw-btn"
                      onClick={handleWithdraw}
                    >
                      <span className="material-icons" style={{ fontSize: '16px' }}>block</span>
                      Withdraw
                    </button>
                  ) : (
                    <button
                      className="finish-btn"
                      onClick={() => setConsentModalOpen(true)}
                      disabled={!generatedPdfFile || processing.isLoadingConsent}
                    >
                      <span className="material-icons" style={{ fontSize: '16px' }}>check_circle</span>
                      Finish
                    </button>
                  )}
                </div>
              </div>
              <div className="panel-content">
                {generatedPdfUrl ? (
                  <DocumentViewer
                    key={generatedPdfUrl}
                    file={generatedPdfUrl}
                    fileType="pdf"
                    height={600}
                    showToolbar={true}
                    onError={(error) => console.error('PDF viewer error:', error)}
                  />
                ) : (
                  <div className="pdf-viewer hexagon-pattern">
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <p style={{ color: 'var(--stone-grey)' }}>No generated content yet</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="hexagon-pattern" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: '100%', flexDirection: 'column', textAlign: 'center', padding: '40px'
          }}>
            <span className="material-icons" style={{
              fontSize: '96px', color: 'rgba(255,255,255,0.3)', marginBottom: '24px'
            }}>description</span>
            <h2 style={{
              color: 'rgba(255,255,255,0.8)', fontSize: '24px', fontWeight: '500', marginBottom: '16px'
            }}>Create Your Professional Profile</h2>
            <p style={{
              color: 'rgba(255,255,255,0.6)', fontSize: '16px', maxWidth: '400px', lineHeight: '1.6'
            }}>Upload your CV from the sidebar to generate a professional profile aligned with SSI's standard format</p>
          </div>
        )}
      </div>

      <ConsentModal
        open={consentModalOpen}
        onConfirm={handleConsent}
        onCancel={() => setConsentModalOpen(false)}
        isSubmitting={isSubmittingConsent}
      />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ContractorProfile;
